import React, { useEffect, useRef } from 'react';
import DeckGL from '@deck.gl/react';
import { OrthographicController, OrthographicView } from '@deck.gl/core';
import { PolygonLayer } from '@deck.gl/layers';
import { tap } from '../../../utils';
import { PickingInfoPoint } from '../math/PickingInfoPoint';
const DeckGLView = ({ hex2dLayerContext, highlightLayerContext, tool, viewStateContext }) => {
    const { viewState, editorState, setViewState, setInteractionState, updateEditorState } = viewStateContext;
    const { highlightLayerData } = highlightLayerContext;
    const highlightLayer = new PolygonLayer(highlightLayerData);
    const hex2dLayer = new PolygonLayer(hex2dLayerContext);
    const getTooltip = (info) => {
        if (!info.coordinate)
            return null;
        const { x, y } = PickingInfoPoint.toRoundedOffsetPoint(info.coordinate);
        return `(${x},${y})`;
    };
    const ref = useRef(null);
    // Emitting extra hover events on shift change for tools that support shift actions,
    // Consider exploring the DeckGL ref to drive events via their emitter. Would be better to
    // set an editorState bit and then have a new hover event be emitted later from DeckGL.
    useEffect(() => {
        if (!editorState.priorHoverEvent)
            return;
        const { info, event } = editorState.priorHoverEvent;
        const handler = (e) => {
            if (e.key !== 'Shift')
                return;
            const syntheticEvent = { ...event, srcEvent: { ...event.srcEvent, shiftKey: e.type === 'keydown' } };
            tool?.onHover?.(info, syntheticEvent);
        };
        window.addEventListener('keydown', handler);
        window.addEventListener('keyup', handler);
        return () => {
            window.removeEventListener('keydown', handler);
            window.removeEventListener('keyup', handler);
        };
    }, [ref, tool, editorState.priorHoverEvent]);
    useEffect(() => {
        if (!ref.current?.deck?.getCanvas())
            return;
        setViewState({
            ...viewState,
            width: ref.current.deck.width,
            height: ref.current.deck.height,
        });
    }, [ref.current?.deck?.getCanvas()]);
    return (React.createElement(DeckGL, { ref: ref, useDevicePixels: false, controller: {
            dragPan: !Boolean(tool.disablePan),
            type: OrthographicController,
        }, initialViewState: viewState, views: new OrthographicView(), layers: [hex2dLayer, highlightLayer], onViewStateChange: (params) => {
            setViewState(params.viewState);
            setInteractionState(params.interactionState);
        }, onDragStart: tap(tool.onDragStart, { after: (info, event) => updateEditorState({
                priorDragStartEvent: { info, event },
                priorDragEvent: { info, event },
                priorGestureEvent: { info, event }
            }) }), onDrag: tap(tool.onDrag, { after: (info, event) => updateEditorState({
                priorDragEvent: { info, event },
                priorGestureEvent: { info, event },
            }) }), onDragEnd: tap(tool.onDragEnd, { after: (info, event) => updateEditorState({
                priorDragEvent: undefined,
                priorDragEndEvent: { info, event },
                priorGestureEvent: { info, event },
            }) }), onHover: tap(tool.onHover, {
            after: (info, event) => updateEditorState({ priorHoverEvent: { info, event } }),
        }), onClick: tap(tool.onClick, {
            after: (info, event) => updateEditorState({ priorClickEvent: { info, event }, priorGestureEvent: { info, event } }),
        }), getTooltip: getTooltip }));
};
export default DeckGLView;
//# sourceMappingURL=DeckGLView.js.map